import React, { useCallback, useEffect, useState } from 'react';
import { matchesSearch } from '../utils/search';
import { useBatchedList } from '../hooks/useBatchedList';
import { useIsMobile } from '../hooks/useIsMobile';
import LazyLoadTrigger from '../components/ui/LazyLoadTrigger';
import { ArrowDownTrayIcon, ChevronDownIcon, ChevronUpIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../hooks/usePageTitle';
import { trainingSessionAPI, exportsAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { getErrorMessage } from '../utils/errorMessage';
import type { PlayerTrainingStats } from '../types/trainingSession';
import { useAuth } from '../contexts/AuthContext';
import { isStaff } from '../types/auth';
import AgeGroupSelect from '../components/common/AgeGroupSelect';
import FilterAccordion from '../components/ui/FilterAccordion';

type SortField = 'playerName' | 'ageGroup' | 'recordedTrainings' | 'presentCount' | 'absentCount' | 'attendanceRate';
type SortDir = 'asc' | 'desc';

const rateColor = (rate: number) => {
  if (rate >= 75) return 'bg-green-500';
  if (rate >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' }) : '–';

/**
 * Edzéslátogatottság a rögzített jelenlét alapján. A stáb a korosztályai
 * játékosait látja, a játékos és a szülő csak a sajátját (gyermekeiét).
 */
const TrainingStatistics: React.FC = () => {
  usePageTitle('Edzés statisztika');
  const { user } = useAuth();
  const staff = isStaff(user);

  const [stats, setStats] = useState<PlayerTrainingStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [ageGroupId, setAgeGroupId] = useState<number | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortField, setSortField] = useState<SortField>(staff ? 'attendanceRate' : 'playerName');
  const [sortDir, setSortDir] = useState<SortDir>(staff ? 'desc' : 'asc');
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();
  const { showToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: { ageGroupId?: number; dateFrom?: string; dateTo?: string } = {};
      if (ageGroupId) params.ageGroupId = ageGroupId;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      setStats(await trainingSessionAPI.getStats(params));
    } catch (err) {
      console.error('Az edzés statisztika betöltése nem sikerült:', err);
    } finally {
      setLoading(false);
    }
  }, [ageGroupId, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'playerName' || field === 'ageGroup' ? 'asc' : 'desc');
    }
  };

  const sorted = stats.filter((row) => matchesSearch(search, row.playerName)).sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal, 'hu') : bVal.localeCompare(aVal, 'hu');
    }
    return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });

  // Az összes korosztály kiválasztásakor 800 sor is lehet - adagokban jelenik meg
  const { visible: visibleRows, hasMore, loadMore } = useBatchedList(sorted, 60);

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? (
      <ChevronUpIcon className="h-3 w-3 inline ml-0.5" />
    ) : (
      <ChevronDownIcon className="h-3 w-3 inline ml-0.5" />
    );
  };

  const thClass = 'px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none';

  const RateBar: React.FC<{ rate: number }> = ({ rate }) => (
    <div className="flex items-center gap-2">
      <div className="h-2 w-20 rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full ${rateColor(rate)}`} style={{ width: `${Math.min(100, rate)}%` }} />
      </div>
      <span className="text-sm font-medium text-gray-900 tabular-nums">{rate.toLocaleString('hu-HU')}%</span>
    </div>
  );

  const activeFilters = (ageGroupId ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  const handleExport = async () => {
    setExporting(true);
    try {
      // Dátum nélkül a szerver az aktuális szezont (augusztus 1-től) adja
      await exportsAPI.attendance({
        ageGroupId: ageGroupId ?? undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
    } catch (err) {
      showToast(getErrorMessage(err, 'Az exportálás nem sikerült.'), 'error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edzés statisztika</h1>
          <p className="hidden sm:block text-sm text-gray-500 mt-1">
            {staff
              ? 'Edzéslátogatottság a rögzített jelenlét alapján.'
              : 'Az edzéseken való részvétel a rögzített jelenlét alapján.'}
          </p>
        </div>
        {staff && (
          <button
            type="button"
            onClick={handleExport}
            // Rögzített jelenlét nélkül üres táblázat lenne - ilyenkor nem kínáljuk
            disabled={exporting || loading || stats.length === 0}
            title={
              stats.length === 0
                ? 'Ebben a szűrésben még nincs rögzített jelenlét, így nincs mit exportálni.'
                : 'Korosztályonként egy munkalap: játékos × edzés/meccs. Dátum nélkül az aktuális szezon.'
            }
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            {exporting ? 'Exportálás...' : 'Jelenléti ív (Excel)'}
          </button>
        )}
      </div>

      <div className="mb-6">
        <FilterAccordion title="Szűrők" activeCount={activeFilters}>
          <div className="flex flex-col gap-3 p-3 bg-white rounded-lg shadow-sm md:flex-row md:flex-wrap md:items-center">
            <FunnelIcon className="hidden md:block h-5 w-5 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Játékos neve..."
              aria-label="Keresés játékos neve szerint"
              className="w-full md:w-48 px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
            {staff && (
              <AgeGroupSelect value={ageGroupId} onChange={setAgeGroupId} placeholder="Összes korosztály" />
            )}
            <div className="flex items-center gap-2">
              <label htmlFor="training-stats-from" className="text-sm text-gray-600">Tól:</label>
              <input
                id="training-stats-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full md:w-auto px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="training-stats-to" className="text-sm text-gray-600">Ig:</label>
              <input
                id="training-stats-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full md:w-auto px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
            {activeFilters > 0 && (
              <button
                type="button"
                onClick={() => {
                  setAgeGroupId(null);
                  setDateFrom('');
                  setDateTo('');
                }}
                className="text-sm text-primary-600 hover:underline text-left"
              >
                Szűrők törlése
              </button>
            )}
          </div>
        </FilterAccordion>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      ) : stats.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          Még nincs rögzített jelenlét ebben az időszakban.
          {staff && (
            <p className="mt-1 text-sm">Az edzés megnyitásakor a „Jelenlét” résznél lehet rögzíteni.</p>
          )}
        </div>
      ) : (
        <>
          {/* Mobil kártyalista */}
          {sorted.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-500">Nincs a keresésnek megfelelő játékos.</p>
          )}
          {isMobile && (
          <div className="grid grid-cols-1 gap-3">
            {visibleRows.map((row) => (
              <div key={row.playerId} className="bg-white rounded-lg shadow border p-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-gray-900 break-words">{row.playerName}</div>
                    <div className="text-xs text-gray-500">{row.ageGroup}</div>
                  </div>
                  <RateBar rate={row.attendanceRate} />
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-600">
                  <span>
                    <span className="text-gray-400">Edzés: </span>
                    {row.recordedTrainings}
                  </span>
                  <span className="text-green-700">{row.presentCount} jelen</span>
                  <span className="text-red-700">{row.absentCount} hiányzott</span>
                  <span>
                    <span className="text-gray-400">Utolsó: </span>
                    {formatDate(row.lastTrainingAt)}
                  </span>
                </div>
              </div>
            ))}
            {hasMore && <LazyLoadTrigger key={visibleRows.length} onLoadMore={loadMore} />}
          </div>
          )}

          {/* Desktop táblázat */}
          {!isMobile && sorted.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={`${thClass} text-left`} onClick={() => handleSort('playerName')}>
                      Játékos <SortIcon field="playerName" />
                    </th>
                    <th className={`${thClass} text-left`} onClick={() => handleSort('ageGroup')}>
                      Korosztály <SortIcon field="ageGroup" />
                    </th>
                    <th className={`${thClass} text-right`} onClick={() => handleSort('recordedTrainings')}>
                      Edzés <SortIcon field="recordedTrainings" />
                    </th>
                    <th className={`${thClass} text-right`} onClick={() => handleSort('presentCount')}>
                      Jelen <SortIcon field="presentCount" />
                    </th>
                    <th className={`${thClass} text-right`} onClick={() => handleSort('absentCount')}>
                      Hiányzott <SortIcon field="absentCount" />
                    </th>
                    <th className={`${thClass} text-left`} onClick={() => handleSort('attendanceRate')}>
                      Részvétel <SortIcon field="attendanceRate" />
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase text-right">Utolsó edzés</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleRows.map((row) => (
                    <tr key={row.playerId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.playerName}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{row.ageGroup}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700 tabular-nums">{row.recordedTrainings}</td>
                      <td className="px-4 py-3 text-sm text-right text-green-700 tabular-nums">{row.presentCount}</td>
                      <td className="px-4 py-3 text-sm text-right text-red-700 tabular-nums">{row.absentCount}</td>
                      <td className="px-4 py-3">
                        <RateBar rate={row.attendanceRate} />
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-500">{formatDate(row.lastTrainingAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasMore && <LazyLoadTrigger key={visibleRows.length} onLoadMore={loadMore} />}
          </div>
          )}
        </>
      )}
    </div>
  );
};

export default TrainingStatistics;
